import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Star, Shield, TrendingUp, Clock, CheckCircle, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.echt.gold';

const Home = () => {
  const [metalPrices, setMetalPrices] = useState(null);

  useEffect(() => {
    fetchMetalPrices();
  }, []);

  const fetchMetalPrices = async () => {
    try {
      const response = await axios.get(`${API}/metal-prices`);
      setMetalPrices(response.data);
    } catch (error) {
      console.error('Error fetching metal prices:', error);
    }
  };

  const services = [
    { title: 'Goldankauf', icon: '🏆', link: '/goldankauf', desc: 'Gold, Altgold, Zahngold' },
    { title: 'Silberankauf', icon: '⚪', link: '/silberankauf', desc: 'Silberbarren, Münzen, Schmuck' },
    { title: 'Uhrenankauf', icon: '⌚', link: '/uhrenankauf', desc: 'Rolex, Omega, Patek Philippe' },
    { title: 'Schmuckankauf', icon: '💎', link: '/schmuckankauf', desc: 'Ringe, Ketten, Armbänder' },
    { title: 'Diamantenankauf', icon: '💠', link: '/diamantenankauf', desc: 'Zertifizierte Diamanten' }
  ];

  const processSteps = [
    { step: '1', title: 'Kontakt aufnehmen', desc: 'Termin vereinbaren oder Versandlabel anfordern' },
    { step: '2', title: 'Bewertung', desc: 'Professionelle Analyse Ihrer Wertgegenstände' },
    { step: '3', title: 'Angebot erhalten', desc: 'Faire Preise basierend auf aktuellem Tageskurs' },
    { step: '4', title: 'Auszahlung', desc: 'Sofortige Barauszahlung oder Überweisung' }
  ];

  return (
    <>
      <Helmet>
        <title>Echt Gold - Uhren-, Schmuck- & Gold- und Silberankauf in NRW</title>
        <meta name="description" content="Seriöser Gold- und Silberankauf, Platin, Luxusuhren und Schmuck in NRW. Faire Tagespreise, kostenlose Bewertung, diskrete Abwicklung. Jetzt Termin vereinbaren!" />
        <meta property="og:title" content="Echt Gold - Uhren-, Schmuck- & Gold- und Silberankauf in NRW" />
        <meta property="og:description" content="Seriöser Gold- und Silberankauf, Platin, Luxusuhren und Schmuck in NRW. Faire Tagespreise, kostenlose Bewertung." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${BASE_URL}/`} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Echt Gold",
          "url": BASE_URL,
          "logo": `${BASE_URL}/logo.png`,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+49-231-39085116",
            "contactType": "customer service",
            "areaServed": "DE",
            "availableLanguage": "German"
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Neben dem Brand 10",
            "addressLocality": "Dortmund",
            "postalCode": "44135",
            "addressCountry": "DE"
          }
        })}
      </script>

      <section className="hero-section" data-testid="hero-section">
        <div className="hero-background"></div>
        <div className="hero-content">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 gold-gradient">
            Ihr vertrauensvoller Partner für<br />Gold- & Silberankauf & Uhrenankauf in NRW
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Faire Preise nach aktuellem Tageskurs | Kostenlose Bewertung | Diskrete & schnelle Abwicklung
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-12">
            <Link to="/kontakt" className="btn-primary" data-testid="cta-termin">Termin vereinbaren</Link>
            <Link to="/kontakt" className="btn-secondary" data-testid="cta-bewertung">Kostenlose Bewertung</Link>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="trust-badge">
              <Star className="text-gold" size={20} fill="#d4af37" />
              <span>4.8/5 Bewertung</span>
            </div>
            <div className="trust-badge">
              <Shield className="text-gold" size={20} />
              <span>Sicher & Diskret</span>
            </div>
            <div className="trust-badge">
              <Award className="text-gold" size={20} />
              <span>15+ Jahre Erfahrung</span>
            </div>
          </div>
        </div>
      </section>

      {metalPrices && (
        <section className="section section-dark" data-testid="price-ticker-section">
          <div className="container">
            <div className="price-ticker">
              <h3 className="text-xl font-bold gold-text mb-4 text-center">
                <TrendingUp className="inline mr-2" size={24} />
                Aktuelle Ankaufspreise (Richtwerte)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="price-item flex-col items-start">
                  <div className="font-semibold gold-text">Gold</div>
                  <div className="text-2xl font-bold">{metalPrices.gold.price} {metalPrices.gold.unit}</div>
                  <div className="text-sm text-green-400">{metalPrices.gold.change}</div>
                </div>
                <div className="price-item flex-col items-start">
                  <div className="font-semibold gold-text">Silber</div>
                  <div className="text-2xl font-bold">{metalPrices.silver.price} {metalPrices.silver.unit}</div>
                  <div className="text-sm text-red-400">{metalPrices.silver.change}</div>
                </div>
                <div className="price-item flex-col items-start">
                  <div className="font-semibold gold-text">Platin</div>
                  <div className="text-2xl font-bold">{metalPrices.platinum.price} {metalPrices.platinum.unit}</div>
                  <div className="text-sm text-green-400">{metalPrices.platinum.change}</div>
                </div>
                <div className="price-item flex-col items-start">
                  <div className="font-semibold gold-text">Palladium</div>
                  <div className="text-2xl font-bold">{metalPrices.palladium.price} {metalPrices.palladium.unit}</div>
                  <div className="text-sm text-green-400">{metalPrices.palladium.change}</div>
                </div>
              </div>
              <p className="text-sm text-gray-400 text-center mt-4">
                <Clock size={14} className="inline mr-1" />
                Preise richten sich nach dem aktuellen Tageskurs und dienen als Orientierung
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="section section-darker" data-testid="services-section">
        <div className="container">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 gold-text">Was wir ankaufen</h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            Von Gold und Silber bis Luxusuhren - wir kaufen Ihre Wertgegenstände zu fairen Preisen
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <Link to={service.link} key={idx} className="card" data-testid={`service-${idx}`}>
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2 gold-text">{service.title}</h3>
                <p className="text-gray-400">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-dark" data-testid="process-section">
        <div className="container">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 gold-text">So einfach funktioniert's</h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            In nur 4 Schritten zu Ihrer Auszahlung
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, idx) => (
              <div key={idx} className="card text-center" data-testid={`process-step-${idx}`}>
                <div className="w-16 h-16 bg-gold/20 border-2 border-gold rounded-full flex items-center justify-center text-2xl font-bold gold-text mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold mb-2 gold-text">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 bg-gold/10 border border-gold/30 rounded-lg max-w-3xl mx-auto">
            <p className="text-center text-sm text-gray-300">
              <Shield className="inline mr-2" size={18} />
              <strong>Wichtiger Hinweis:</strong> Gemäß Geldwäschegesetz ist eine Legitimationsprüfung (Ausweis) bei allen Ankäufen erforderlich.
            </p>
          </div>
        </div>
      </section>

      <section className="section section-darker" data-testid="cities-section">
        <div className="container">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 gold-text">Wir sind in ganz NRW für Sie da</h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            Vor-Ort-Termine in allen großen Städten nach Vereinbarung
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-center">
            {['aachen', 'bielefeld', 'bochum', 'bonn', 'bottrop', 'dortmund', 'duisburg', 'duesseldorf', 
              'essen', 'gelsenkirchen', 'hagen', 'hamm', 'herne', 'koeln', 'krefeld', 'leverkusen',
              'moenchengladbach', 'muelheim-an-der-ruhr', 'muenster', 'oberhausen', 'remscheid', 'solingen', 'wuppertal'].map((city, idx) => (
              <Link 
                key={idx} 
                to={`/ankauf-${city}`} 
                className="p-4 bg-black/40 border border-gold/20 rounded-lg hover:border-gold/50 hover:bg-gold/5 transition-all"
                data-testid={`city-link-${idx}`}
              >
                <span className="text-gray-300 hover:text-gold transition-colors capitalize">
                  {city.replace('-', ' ').replace('duesseldorf', 'Düsseldorf').replace('koeln', 'Köln').replace('moenchengladbach', 'Mönchengladbach').replace('muelheim', 'Mülheim').replace('muenster', 'Münster')}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-dark" data-testid="cta-section">
        <div className="container">
          <div className="card text-center max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 gold-text">Bereit für eine kostenlose Bewertung?</h2>
            <p className="text-gray-400 mb-8 text-lg">
              Kontaktieren Sie uns noch heute und erhalten Sie ein unverbindliches Angebot
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary" data-testid="cta-contact">Jetzt Kontakt aufnehmen</Link>
              <a href="tel:023139085116" className="btn-secondary" data-testid="cta-call">0231 39085116</a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;